import React, { useMemo } from 'react';
import { Table, Tooltip, Button, Space } from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  SendOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { getTypeColor } from '../constants/fileTypes';
import { TYPE_ABBREVIATIONS } from '../constants/documentTypes';
import { StyledTable } from '../constants/styles';

const MobileActionsSpace = ({ children }) => (
  <Space size="small" style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
    {children}
  </Space>
);

const DocumentTable = ({
  documents,
  filteredDocuments,
  sortedInfo,
  handleChange,
  handlePreview,
  handleDownload,
  handleDelete,
  handleEmailDocument,
  handleMetadataUpdate,
  loading,
  error,
  isMobile,
}) => {
  const columns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
      render: (text, record) => (
        <div style={{
          maxWidth: isMobile ? '120px' : 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          <button 
            onClick={() => handlePreview(record)}
            className="document-link"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            {text}
          </button>
        </div>
      ),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      sorter: (a, b) => a.company.localeCompare(b.company),
      sortOrder: sortedInfo.columnKey === 'company' && sortedInfo.order,
      responsive: ['md'],
      render: (text) => (
        <span style={{ color: '#4b5563', fontWeight: 500 }}>{text}</span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type.localeCompare(b.type),
      sortOrder: sortedInfo.columnKey === 'type' && sortedInfo.order,
      responsive: ['md'],
      render: (text) => {
        const abbreviatedText = TYPE_ABBREVIATIONS[text] || text;

        return (
          <Tooltip title={text} placement="topLeft">
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: getTypeColor(text).bg,
                color: getTypeColor(text).text,
                fontWeight: 500,
                border: '1px solid rgba(0, 0, 0, 0.05)',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'inline-block',
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {isMobile ? abbreviatedText : text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      sorter: (a, b) => {
        const dateA = new Date(a.uploadDate);
        const dateB = new Date(b.uploadDate);
        return dateB - dateA;
      },
      sortOrder: sortedInfo.columnKey === 'uploadDate' && sortedInfo.order,
      responsive: ['md'],
      render: (text) => {
        const formatDate = (dateString) => {
          const date = new Date(dateString);
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          });
        };

        return (
          <Tooltip title={formatDate(text)}>
            <span style={{ color: '#4b5563', fontWeight: 500 }}>
              {formatDate(text)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const supportedTypes = ['.pdf'];
        const fileExtension = record.name.substring(record.name.lastIndexOf('.')).toLowerCase();
        const isPreviewSupported = supportedTypes.includes(fileExtension);

        return (
          <MobileActionsSpace>
            <Tooltip 
              title="Preview Document (PDF Only)" 
              mouseEnterDelay={1.5}
            >
              <Button
                icon={<EyeOutlined />}
                onClick={() => handlePreview(record)}
                disabled={!isPreviewSupported}
                style={{
                  opacity: isPreviewSupported ? 1 : 0.5,
                  cursor: isPreviewSupported ? 'pointer' : 'not-allowed',
                  backgroundColor: 'white',
                  color: '#3b82f6',
                  borderColor: '#3b82f6',
                }}
                className={isPreviewSupported ? '' : 'no-hover-effect'}
              />
            </Tooltip>
            
            <Tooltip 
              title="Download Document" 
              mouseEnterDelay={2}
            >
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
                style={{
                  backgroundColor: 'white',
                  color: '#48bb78',
                  borderColor: '#48bb78',
                }}
              />
            </Tooltip>
            
            <Tooltip 
              title="Email Document" 
              mouseEnterDelay={2}
            >
              <Button
                icon={<SendOutlined />}
                onClick={() => handleEmailDocument(record)}
                style={{
                  backgroundColor: 'white',
                  color: '#805ad5',
                  borderColor: '#805ad5',
                }}
              />
            </Tooltip>
            
            <Tooltip 
              title="Delete Document" 
              mouseEnterDelay={2}
            >
              <Button
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.name)}
                style={{
                  backgroundColor: 'white',
                  color: '#f56565',
                  borderColor: '#f56565',
                }}
              />
            </Tooltip>
            
            <Tooltip 
              title="Edit Document Details" 
              mouseEnterDelay={2}
            >
              <Button
                icon={<EditOutlined />}
                onClick={() => handleMetadataUpdate(record)}
                style={{
                  backgroundColor: 'white',
                  color: '#ed8936',
                  borderColor: '#ed8936',
                }}
              />
            </Tooltip>
          </MobileActionsSpace>
        );
      },
    },
  ], [sortedInfo, isMobile, handleDelete, handleDownload, handlePreview, handleEmailDocument, handleMetadataUpdate]);

  return (
    <StyledTable
      columns={columns}
      dataSource={filteredDocuments}
      rowKey="id"
      pagination={{
        pageSize: isMobile ? 5 : 10,
        size: isMobile ? 'small' : 'default',
        simple: isMobile
      }}
      onChange={handleChange}
      size={isMobile ? 'small' : 'middle'}
      scroll={{ y: 'calc(100vh - 400px)' }}
      locale={{
        emptyText: loading ? (
          <div style={{ display: 'none' }} />
        ) : error ? (
          <div style={{ padding: '24px' }}>
            <span style={{ color: '#ff4d4f' }}>{error}</span>
          </div>
        ) : (
          'No documents found'
        )
      }}
    />
  );
};

export default DocumentTable;